import React, {useRef} from 'react';
import clsx from 'clsx';
import {Button, Typography} from '@mui/material';
import {Alert} from '@netx/core-components/Alert';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {LegacyFormTextField as TextField} from '@netx/core-components/Form/TextField';
import useForm from '@netx/core-components/Form/hooks/useForm';
import {
	LegacyForm as Form,
	type LegacyFormProps as FormProps,
} from '@netx/core-components/Form/LegacyForm';
import {getInputFormObject} from '@netx/core-components/Form/util';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {SubmitButton} from '@netx/core-components/SubmitButton';
import {useResetPassword} from '../../hooks';
import {
	AuthFieldLabel,
	AuthMessage,
	AuthTitle,
	AuthActionLabel,
} from '../../locales';
import type {ResetPasswordRequestFormData} from '../../types';
import {ResetPasswordRequestSuccess} from './ResetPasswordRequestSuccess';
import {
	resetPasswordRequestClasses,
	getResetPasswordRequestUtilityClass,
	ResetPasswordRequestClasses,
	RESET_PASSWORD_REQUEST_CLASS_NAME,
} from './resetPasswordRequestClasses';

export type ResetPasswordRequestOwnerState = WithClasses<
	ResetPasswordRequestClasses,
	{}
>;

const useUtilityClasses = (ownerState: ResetPasswordRequestOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getResetPasswordRequestUtilityClass, classes);
};

const ResetPasswordRequestRoot = styled('div', {
	name: RESET_PASSWORD_REQUEST_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.flexFillColumn,
	alignItems: 'center',
	width: '100%',
}));

const ResetPasswordRequestHeader = styled('header')(({theme}) => ({
	flex: '0 0 auto',
	padding: '24px 16px',
}));

const ResetPasswordRequestTextField = styled(TextField, {
	name: RESET_PASSWORD_REQUEST_CLASS_NAME,
	slot: 'Field',
})(({theme}) => ({
	input: {
		paddingLeft: theme.spacing(1.5),
	},
	'& + &': {
		marginTop: theme.spacing(2),
	},
}));

const ResetPasswordRequestContent = styled(ScrollBorder)(({theme}) => ({
	[`& .${scrollBorderClasses.scrollWrapper}`]: {
		padding: theme.spacing(0, 4, 3, 4),
	},
}));

const ResetPasswordRequestForm = styled(Form)(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	width: '100%',
})) as (props: FormProps<ResetPasswordRequestFormData>) => JSX.Element;

const ResetPasswordRequestFooter = styled('footer')(({theme}) => ({
	flex: '0 0 auto',
	alignItems: 'center',
	display: 'flex',
	width: '100%',
	justifyContent: 'space-between',
	padding: '24px',
}));

const ResetPasswordRequestAlert = styled(Alert, {
	name: RESET_PASSWORD_REQUEST_CLASS_NAME,
	slot: 'Alert',
})(({theme}) => ({
	justifyContent: 'center',
	position: 'relative',
	marginBottom: theme.spacing(2),
	width: '100%',
}));

export type ResetPasswordRequestProps = WithClasses<
	ResetPasswordRequestClasses,
	{
		onCancel?: () => void;
	}
>;

export const ResetPasswordRequestPrimitive = ({
	classes: _classes,
	className,
	onCancel,
	...props
}: ResetPasswordRequestProps) => {
	const {resetPassword, resettingPassword, passwordReset, error} =
		useResetPassword();

	const form = useForm<ResetPasswordRequestFormData>(undefined, {
		onSubmit: resetPassword,
	});
	const inputForm = getInputFormObject(form);

	const ownerState: ResetPasswordRequestOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<ResetPasswordRequestRoot
			className={clsx(classes.root, className)}
			{...props}
		>
			<ResetPasswordRequestHeader>
				<Typography variant="h5">{AuthTitle.FORGOT_PASSWORD}</Typography>
			</ResetPasswordRequestHeader>

			{error ? (
				<ResetPasswordRequestAlert severity="error">
					{AuthMessage.PASSWORD_RESET_REQUEST_FAILED}
				</ResetPasswordRequestAlert>
			) : null}

			{passwordReset ? (
				<ResetPasswordRequestSuccess onCancel={onCancel} />
			) : (
				<ResetPasswordRequestForm form={form}>
					<ResetPasswordRequestContent>
						<Typography variant="body2" sx={{mb: 2}}>
							{AuthMessage.RESET_PASSWORD_REQUEST}
						</Typography>

						<ResetPasswordRequestTextField
							disabled={form.isSubmitting}
							form={inputForm}
							name="login"
							label={AuthFieldLabel.login}
							required
							value={form.values.login}
						/>
					</ResetPasswordRequestContent>

					<ResetPasswordRequestFooter>
						<Button
							disabled={form.isSubmitting}
							variant="text"
							onClick={() => {
								onCancel?.();
							}}
						>
							{AuthActionLabel.RETURN_TO_LOGIN}
						</Button>
						<SubmitButton
							showWorking={form.isSubmitting}
							disabled={!form.isValid || !form.hasChanged}
							label={AuthActionLabel.SEND_PASSWORD_RESET_EMAIL}
						/>
					</ResetPasswordRequestFooter>
				</ResetPasswordRequestForm>
			)}
		</ResetPasswordRequestRoot>
	);
};

export const ResetPasswordRequest = ResetPasswordRequestPrimitive;

export default ResetPasswordRequest;
